import { Avatar } from "components/Avatar"
import { ProjectBuildCommentDetail } from "../project-build.type"
import DotCard from "pages/conversations/atoms/DotCard"
import {
  formatDateForConversationMessage,
  removeTimestampInFileName,
} from "helpers/utils"
import { useEffect, useMemo, useState } from "react"
import { useBoolean } from "helpers/hooks"
import {
  ApprovalStatus,
  MODEL_ACTIVITY_LOG,
} from "components/activity-log/activity-log.type"
import { PuffLoader } from "react-spinners"
import Tippy from "@tippyjs/react"
import { ApprovalLogCard } from "pages/conversations/molecules/ApprovalLogCard"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { getAccessTokenInLocalStorage } from "helpers/access_token"
import { downloadQueueManager } from "components/DownloadProcess/downloadQueue"
import { v4 as uuid } from "uuid"
import { postApprovalLogMiddleWare } from "pages/conversations/conversations.api"
import { cloneDeep } from "lodash"
import { getPendingApprovalsMiddleware } from "pages/layout/services/api"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { TippyCustomzie } from "components/TippyCustomzie"
import ActionItem from "pages/projects/molecules/ActionItem"
import { ReactComponent as SelectIcon } from "assets/images/icons/icon-arrow-down-select-tippy.svg"

interface Props {
  comment: ProjectBuildCommentDetail
  idUserInfo: string
  subNameDownload: string
  isActiveComment?: boolean
  onReply?: (comment: ProjectBuildCommentDetail) => void
  isOwner: boolean
  handlrScrollCommmentFrom: (commentFromId: string) => void

}
export const CommentBuildItemCard = (props: Props) => {
  const { comment, idUserInfo, subNameDownload, isActiveComment, onReply, isOwner, handlrScrollCommmentFrom } = props
  const [processedContent, setProcessedContent] = useState(comment.content)
  const [isProcessing, setIsProcessing] = useState(false)
  const showCommentMenu = useBoolean(false)

  const handleReply = () => {
    if (onReply) {
      onReply(comment)
    }
    showCommentMenu.setValue(false)
  }
  const [listApprovalLogs, setListApprovalLogs] = useState<
    {
      content: ApprovalStatus
      created_at: string
      user: {
        avatar: string
        id: string
        fullname: string
        email: string
      }
    }[]
  >([])
  useEffect(() => {
    if (!comment.approval_logs) return
    setListApprovalLogs(comment.approval_logs)
  }, [JSON.stringify(comment.approval_logs)])

  // Extract files from content based on file IDs
  const filesFromContent = useMemo(() => {
    if (!comment.content || !comment.files) return []

    // Extract file IDs from img tags in content
    const imgRegex = /<img[^>]+src="([^"]+)"[^>]*>/g
    const fileIds: string[] = []
    let match

    while ((match = imgRegex.exec(comment.content)) !== null) {
      fileIds.push(match[1])
    }
    // Match file IDs with messages.files
    const matchedFiles = comment.files.filter((file) =>
      fileIds.includes(file.id)
    )

    return matchedFiles
  }, [comment.content, comment.files])

  // Process content to replace file IDs with base64 images
  useEffect(() => {
    const processContent = async () => {
      if (!filesFromContent.length || !comment.messageKey) {
        setProcessedContent(comment.content)
        return
      }
      setIsProcessing(true)
      try {
        const processedFiles = await Promise.all(
          filesFromContent.map(async (file) => {
            try {
              const base64Str =
                await encryptionGCMHelper.getFileImageFromEncrypted(
                  file.file,
                  comment.messageKey,
                  getAccessTokenInLocalStorage(),
                  {
                    project_build_id: comment.id,
                  },
                  "base64",
                  undefined
                )
              return {
                ...file,
                base64Str: base64Str as string,
              }
            } catch (error) {
              console.error("Error decrypting file:", error)
              return {
                ...file,
                base64Str: "",
              }
            }
          })
        )

        // Replace file IDs in content with base64 strings
        let newContent = comment.content
        processedFiles.forEach((file) => {
          if (file.base64Str) {
            newContent = newContent.replace(file.id, file.base64Str)
          }
        })

        setProcessedContent(newContent)
      } catch (error) {
        console.error("Error processing content:", error)
        setProcessedContent(comment.content)
      } finally {
        setIsProcessing(false)
      }
    }

    processContent()
  }, [JSON.stringify(filesFromContent)])

  const handleApprove = async () => {
    const result = await postApprovalLogMiddleWare(
      {
        comment_id: comment.id,
        model: MODEL_ACTIVITY_LOG.PROJECT_BUILDS,
        status: ApprovalStatus.APPROVED,
        user_ids: JSON.stringify([idUserInfo]),
      },
      "",
      false
    )
    if (result) {
      getPendingApprovalsMiddleware()
      const newListApprovalLogs = cloneDeep(listApprovalLogs).concat(result)
      setListApprovalLogs(newListApprovalLogs)
      toast(
        <LabelNotificationPage
          messenger="Approved successfully"
          type="success"
        />
      )
    } else {
      toast(
        <LabelNotificationPage
          messenger="This action is unavailable because the approval request has already been cancelled."
          type="error"
        />
      )
    }
  }
  const handleReject = async () => {
    const result = await postApprovalLogMiddleWare(
      {
        comment_id: comment.id,
        model: MODEL_ACTIVITY_LOG.PROJECT_BUILDS,
        status: ApprovalStatus.REJECTED,
        user_ids: JSON.stringify([idUserInfo]),
      },
      "",
      false
    )
    if (result) {
      getPendingApprovalsMiddleware()
      const newListApprovalLogs = cloneDeep(listApprovalLogs).concat(result)
      setListApprovalLogs(newListApprovalLogs)
      toast(
        <LabelNotificationPage
          messenger="Rejected successfully"
          type="success"
        />
      )
    } else {
      toast(
        <LabelNotificationPage
          messenger="This action is unavailable because the approval request has already been cancelled."
          type="error"
        />
      )
    }
  }
  const handleCancellation = async () => {
    const result = await postApprovalLogMiddleWare(
      {
        comment_id: comment.id,
        model: MODEL_ACTIVITY_LOG.PROJECT_BUILDS,
        status: ApprovalStatus.CANCELLED,
        user_ids: JSON.stringify([idUserInfo]),
      },
      "",
      false
    )
    if (result) {
      getPendingApprovalsMiddleware()
      const newListApprovalLogs = cloneDeep(listApprovalLogs).concat(result)
      setListApprovalLogs(newListApprovalLogs)
      toast(
        <LabelNotificationPage
          messenger="Cancellation successfully"
          type="success"
        />
      )
    } else {
      toast(
        <LabelNotificationPage messenger="Cancellation failed" type="error" />
      )
    }
  }

  return (
    <div
      className="p-2 flex flex-col mb-3 cursor-pointer relative group"
      id={`msg-${comment.id}`}
      style={{
        backgroundColor: "#F7F7F7",
        borderRadius: 6,
      }}
    >
      {/* Reply Bar - comment_from */}
      {comment.comment_from ? (
        <div style={{ marginBottom: 8, marginRight: isActiveComment && onReply ? "40px" : "0px" }} onClick={() => handlrScrollCommmentFrom(comment?.comment_from?.id || '')}>
          <div
            style={{
              background: "rgba(86, 160, 18, 0.15)",
              borderLeft: "4px solid rgb(86, 160, 18)",
              padding: "6px",
              borderRadius: "4px",
            }}
          >
            <div className="flex items-start">
              <div className="flex-1 min-w-0">
                <p
                  className="font-bold text-13 leading-22"
                  style={{
                    color: "#111111",
                    marginBottom: 4,
                  }}
                >
                  {comment.comment_from.user.fullname} - {comment.comment_from.user.email}
                </p>
                <p
                  className="font-normal text-12 leading-18"
                  style={{
                    color: "#111111",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {(() => {
                    const tmp = document.createElement("DIV")
                    tmp.innerHTML = comment.comment_from.content
                    return tmp.textContent || tmp.innerText || ""
                  })()}
                </p>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Message Menu Icon */}
      {isActiveComment && onReply && (
        <div className="absolute top-2 right-2" style={{ zIndex: 10 }}>
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="bottom-end"
            interactive
            arrow={false}
            animation="scale"
            visible={showCommentMenu.value}
            onClickOutside={() => showCommentMenu.setValue(false)}
            content={
              <div
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  minWidth: 150,
                }}
              >
                <ActionItem
                  title="Reply"
                  onClick={handleReply}
                  icon={
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10 4L6 8L10 12"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                />
              </div>
            }
            allowHTML
          >
            <div
              className="flex p-[6px] hover-action-icon cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                showCommentMenu.setValue(true)
              }}
            >
              <SelectIcon />
            </div>
          </TippyCustomzie>
        </div>
      )}

      <div className="flex items-center mb-2">
        <Avatar
          width="24px"
          height="24px"
          name={comment.user.fullname}
          avatar={comment.user.avatar}
          textcolor="white"
          contentsize="11px"
          justOneLetter={true}
        />
        <p
          className="font-semibold text-13 leading-22 color-black-111111 pl-2"
          style={{
            whiteSpace: "nowrap",
          }}
        >
          {comment.user.fullname}
        </p>
        {comment.user.email ? (
          <div className="hidden md:flex md:items-center">
            <span className="pl-1"> - </span>
            <p
              className="font-semibold text-13 leading-22 color-black-111111 pl-1"
              style={{
                whiteSpace: "nowrap",
              }}
            >
              {comment.user.email}
            </p>
          </div>
        ) : null}
        <DotCard />
        <p className="text-12 leading-18 color-gray-7a font-normal">
          {comment.created_at
            ? `${formatDateForConversationMessage(comment.created_at)}`
            : "No update"}
        </p>
      </div>
      {isProcessing ? (
        <PuffLoader color="#F7AC1B" loading={true} size={30} />
      ) : (
        <div
          style={{
            fontWeight: 400,
            fontSize: 13,
            lineHeight: "22px",
            color: "#111111",
          }}
          className="break-words message-item"
          dangerouslySetInnerHTML={{
            __html: processedContent,
          }}
        ></div>
      )}
      {comment.files && comment.files.length ? (
        <div className="flex items-center flex-wrap mt-2">
          {comment.files.map((file, index) => (
            <div
              key={index}
              onClick={() => {
                downloadQueueManager.add({
                  apiURL: file.file,
                  downloadId: uuid(),
                  name: removeTimestampInFileName(file.file_name),
                  subName: subNameDownload,
                  isFileOne: true,
                  dataFileOne: {
                    assetPath: file.file,
                    decryptedKey: comment.messageKey,
                  },
                })
              }}
            >
              <Tippy
                className="custom-tippy-menu-left"
                placement="top"
                content={
                  <span
                    className="font-normal"
                    style={{
                      fontSize: 13,
                      lineHeight: "22px",
                      wordBreak: "break-word",
                    }}
                  >
                    {removeTimestampInFileName(file.file_name)}
                  </span>
                }
              >
                <div
                  className="flex items-center h-7 px-2 mr-2 mb-1 cursor-pointer"
                  style={{
                    background: "#F1F1F1",
                    border: "1px solid #E4E4E4",
                    borderRadius: 4,
                    maxWidth: 200,
                  }}
                >
                  <p
                    className="mr-1"
                    style={{
                      fontWeight: 500,
                      fontSize: 12,
                      lineHeight: "18px",
                      color: "#111111",
                      whiteSpace: "nowrap",
                      display: "inline-block",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {removeTimestampInFileName(file.file_name)}
                  </p>
                </div>
              </Tippy>
            </div>
          ))}
        </div>
      ) : null}
      <ApprovalLogCard
        listApprovalLogs={listApprovalLogs}
        isOwner={isOwner}
        handleCancellation={handleCancellation}
        handleApprove={handleApprove}
        handleReject={handleReject}
        idUserInfo={idUserInfo}
        createdBy={comment.created_by}
      />
    </div>
  )
}
